// src/pages/SolicitudParteTrabajo.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../components/Toast/useToast.jsx";
import { useAuth } from "../contexts/AuthContext";
import { db, serverTimestamp, auth } from "../firebase";
import { doc, setDoc, collection } from "firebase/firestore";
import { v4 as uuid } from "uuid";

// Import utilities
import { isValidEmail, isValidPhone } from "../utils";
import DatePicker from "../components/DatePicker";

export default function SolicitudParteTrabajo() {
  const { showToast } = useToast();
  const { user, companyId } = useAuth();
  const navigate = useNavigate();

  // Estados del formulario
  const [loading, setLoading] = useState(false);
  const [showCreateWorkButton, setShowCreateWorkButton] = useState(false);
  
  // 👥 Estado para operarios
  const [operarios, setOperarios] = useState([]);
  const [operariosLoading, setOperariosLoading] = useState(true);
  const [selectedOperarios, setSelectedOperarios] = useState([]);
  const [filtroOperarios, setFiltroOperarios] = useState(""); // 🔍 Filtro de búsqueda
  
  // 🛡️ Estado para PRL
  const [solicitarPRL, setSolicitarPRL] = useState(false);
  const [responsablePRL, setResponsablePRL] = useState(null);
  const [filtroPRL, setFiltroPRL] = useState("");
  
  // 💰 Estado para Centro de Gastos
  const [solicitarCentroGastos, setSolicitarCentroGastos] = useState(false);
  
  // 🏨 Estado para Hotel
  const [solicitarHotel, setSolicitarHotel] = useState(false);
  
  // 📝 Historial de valores para autocompletar
  const [historialOfertas, setHistorialOfertas] = useState([]);
  const [historialPresupuestos, setHistorialPresupuestos] = useState([]);
  const [historialCodigos, setHistorialCodigos] = useState([]);
  const [historialPartidas, setHistorialPartidas] = useState([]);
  
  const [formData, setFormData] = useState({
    fechaInicio: "",
    fechaFin: "",
    numeroOferta: "",
    numeroPresupuesto: "",
    codigoObra: "",
    partidaTrabajo: "",
    trabajosRealizar: "",
    operariosAsignados: "",
    direccionObra: "",
    personaContacto: "",
    telefonoContacto: "",
    emailCliente: "",
  });

  // Manejar cambios en el formulario
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ===== Cargar operarios (PRIMERO desde JSON actualizado, DESPUÉS intentar BD como extra) =====
  useEffect(() => {
    const loadOperarios = async () => {
      try {
        setOperariosLoading(true);
        console.log('🔄 Cargando operarios...');
        
        // 🎯 ESTRATEGIA: Cargar desde JSON sincronizado diariamente con BaseGes
        console.log('📂 Cargando desde JSON actualizado (sincronizado con BaseGes)...');
        
        // Intentar cargar operarios-v2.json (actualizado diariamente)
        try {
          const response = await fetch('/operarios-v2.json?' + Date.now()); // Cache buster
          if (response.ok) {
            const operarios = await response.json();
            
            if (operarios && operarios.length > 0) {
              setOperarios(operarios);
              console.log(`📊 ${operarios.length} operarios cargados desde JSON sincronizado`);
              showToast({ 
                type: "success", 
                message: `📊 ${operarios.length} operarios cargados desde JSON sincronizado` 
              });
              return; // ✅ SALIR AQUÍ - JSON cargado exitosamente
            }
          }
        } catch (jsonError) {
          console.warn('⚠️ Error cargando JSON operarios:', jsonError);
        }

        // 🔄 FALLBACK: Si JSON falla, intentar BaseGes directo
        console.log('🔄 JSON no disponible, intentando BaseGes directo...');
        
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('Usuario no autenticado para consulta BD');
        }
        
        const idToken = await currentUser.getIdToken();
        
        const operariosUrl = import.meta.env.VITE_GET_OPERARIOS_URL || 
                           'https://getoperarios-vjg6zjyprq-uc.a.run.app';
        
        const response = await fetch(operariosUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.ok && result.operarios?.length > 0) {
            setOperarios(result.operarios);
            showToast({ 
              type: "success", 
              message: `🔥 ${result.operarios.length} operarios desde BaseGes directo (fallback)` 
            });
            return; // ✅ SALIR AQUÍ - BaseGes cargado exitosamente
          }
        }

        // ⚠️ ÚLTIMO RECURSO: Datos mínimos
        console.error('❌ No se pudieron cargar operarios desde ninguna fuente');
        showToast({ 
          type: "error", 
          message: 'Error: No se encontraron fuentes de datos de operarios' 
        });
        
        setOperarios([
          {
            id: 1,
            nombre: "Sistema en configuración",
            telefono: "",
            email: "sistema@cejm.eu",
            categoria: "Sistema",
            especialidad: "CEJM"
          }
        ]);
        
      } catch (error) {
        console.error('❌ Error general:', error);
        setOperarios([]);
      } finally {
        setOperariosLoading(false);
      }
    };

    loadOperarios();
  }, []);

  // ===== Cargar historial desde localStorage =====
  useEffect(() => {
    const loadHistorial = () => {
      try {
        const ofertas = JSON.parse(localStorage.getItem('historialOfertas') || '[]');
        const presupuestos = JSON.parse(localStorage.getItem('historialPresupuestos') || '[]');
        const codigos = JSON.parse(localStorage.getItem('historialCodigos') || '[]');
        const partidas = JSON.parse(localStorage.getItem('historialPartidas') || '[]');
        
        setHistorialOfertas(ofertas);
        setHistorialPresupuestos(presupuestos);
        setHistorialCodigos(codigos);
        setHistorialPartidas(partidas);
        
        // 💾 RESTAURAR datos guardados si existen (después de re-login)
        const draft = localStorage.getItem('solicitudParteTrabajo_draft');
        if (draft) {
          try {
            const { formData: savedFormData, selectedOperarios: savedOperarios, timestamp } = JSON.parse(draft);
            
            // Solo restaurar si el draft es reciente (menos de 1 hora)
            const draftAge = Date.now() - new Date(timestamp).getTime();
            const oneHour = 60 * 60 * 1000;
            
            if (draftAge < oneHour) {
              console.log('📋 Restaurando datos guardados del formulario...');
              setFormData(savedFormData);
              setSelectedOperarios(savedOperarios || []);
              
              showToast({ 
                type: "success", 
                message: "✅ Datos del formulario restaurados. Puedes continuar donde lo dejaste." 
              });
            } else {
              // Limpiar draft antiguo
              localStorage.removeItem('solicitudParteTrabajo_draft');
              console.log('🗑️ Draft antiguo eliminado');
            }
          } catch (err) {
            console.error('Error restaurando draft:', err);
            localStorage.removeItem('solicitudParteTrabajo_draft');
          }
        }
      } catch (error) {
        console.error('Error cargando historial:', error);
      }
    };
    
    loadHistorial();
  }, []);

  // ===== Guardar valor en historial =====
  const saveToHistorial = (key, value) => {
    if (!value || value.trim() === '' || value === 'No aplica') return;
    
    try {
      const storageKey = `historial${key}`;
      const historial = JSON.parse(localStorage.getItem(storageKey) || '[]');
      
      // Evitar duplicados
      if (!historial.includes(value)) {
        const newHistorial = [value, ...historial].slice(0, 10); // Mantener solo los últimos 10
        localStorage.setItem(storageKey, JSON.stringify(newHistorial));
        
        // Actualizar estado correspondiente
        if (key === 'Ofertas') setHistorialOfertas(newHistorial);
        if (key === 'Presupuestos') setHistorialPresupuestos(newHistorial);
        if (key === 'Codigos') setHistorialCodigos(newHistorial);
        if (key === 'Partidas') setHistorialPartidas(newHistorial);
      }
    } catch (error) {
      console.error('Error guardando en historial:', error);
    }
  };

  // Normalizar texto quitando acentos para búsqueda sin tildes
  const norm = (str) => str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  // 🔍 Filtrar operarios por nombre (sin importar acentos)
  const operariosFiltrados = operarios.filter(operario =>
    norm(operario.nombre).includes(norm(filtroOperarios))
  );

  // ===== Manejar selección de operarios =====
  const addOperario = (operario) => {
    if (!operario) return;
    
    // Verificar si ya está seleccionado
    if (selectedOperarios.some(op => op.id === operario.id)) {
      showToast({ 
        type: "warning", 
        message: `${operario.nombre} ya está seleccionado` 
      });
      return;
    }

    // Añadir operario
    const newSelected = [...selectedOperarios, operario];
    setSelectedOperarios(newSelected);

    // Auto-completar campo de operarios asignados
    const operariosText = newSelected.map(op => 
      `${op.nombre}`
    ).join(', ');
    handleChange('operariosAsignados', operariosText);

    showToast({ 
      type: "success", 
      message: `${operario.nombre} añadido al equipo` 
    });
  };

  const removeOperario = (operarioId) => {
    const newSelected = selectedOperarios.filter(op => op.id !== operarioId);
    setSelectedOperarios(newSelected);

    // Actualizar campo de operarios asignados
    const operariosText = newSelected.map(op => 
      `${op.nombre}`
    ).join(', ');
    handleChange('operariosAsignados', operariosText);
  };

  // 🤖 Funciones automáticas
  const generateCodigoObra = () => {
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const codigo = `OBRA-${year}${month}-${random}`;
    handleChange('codigoObra', codigo);
    showToast({ type: "success", message: `Código generado: ${codigo}` });
  };

  const generateNumeroOferta = () => {
    const year = new Date().getFullYear();
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    const numero = `OF-${year}-${random}`;
    handleChange('numeroOferta', numero);
    showToast({ type: "success", message: `Número generado: ${numero}` });
  };

  const generateNumeroPresupuesto = () => {
    const year = new Date().getFullYear();
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    const numero = `PR-${year}-${random}`;
    handleChange('numeroPresupuesto', numero);
    showToast({ type: "success", message: `Presupuesto generado: ${numero}` });
  };

  // 🤖 Funciones para fechas rápidas (rango de fechas)
  const setRangoHoy = () => {
    const hoy = new Date().toISOString().split('T')[0];
    handleChange('fechaInicio', hoy);
    handleChange('fechaFin', hoy);
    showToast({ type: "success", message: "Rango configurado para hoy" });
  };

  const setRangoEstaSemana = () => {
    const hoy = new Date();
    const inicio = new Date(hoy);
    const fin = new Date(hoy);
    
    // Inicio: lunes de esta semana
    const diaActual = inicio.getDay();
    const diasDesdeInicio = diaActual === 0 ? 6 : diaActual - 1;
    inicio.setDate(inicio.getDate() - diasDesdeInicio);
    
    // Fin: viernes de esta semana
    const diasHastaFin = diaActual === 0 ? -2 : 5 - diaActual;
    fin.setDate(fin.getDate() + diasHastaFin);
    
    handleChange('fechaInicio', inicio.toISOString().split('T')[0]);
    handleChange('fechaFin', fin.toISOString().split('T')[0]);
    showToast({ type: "success", message: "Rango configurado para esta semana (L-V)" });
  };

  const setRangoProximaSemana = () => {
    const hoy = new Date();
    const proximoLunes = new Date(hoy);
    const proximoViernes = new Date(hoy);
    
    // Próximo lunes
    const diasHastaLunes = (8 - hoy.getDay()) % 7 || 7;
    proximoLunes.setDate(hoy.getDate() + diasHastaLunes);
    
    // Próximo viernes (4 días después del lunes)
    proximoViernes.setDate(proximoLunes.getDate() + 4);
    
    handleChange('fechaInicio', proximoLunes.toISOString().split('T')[0]);
    handleChange('fechaFin', proximoViernes.toISOString().split('T')[0]);
    showToast({ type: "success", message: "Rango configurado para la próxima semana (L-V)" });
  };

  // Formatear rango de fechas para mostrar y enviar
  const formatRangoFechas = () => {
    if (!formData.fechaInicio) return "";
    const inicio = new Date(formData.fechaInicio).toLocaleDateString('es-ES');
    if (!formData.fechaFin || formData.fechaFin === formData.fechaInicio) return inicio;
    const fin = new Date(formData.fechaFin).toLocaleDateString('es-ES');
    return `${inicio} - ${fin}`;
  };

  // Validar formulario
  const validateForm = () => {
    const required = [
      'fechaInicio',
      'fechaFin',
      'codigoObra', 
      'partidaTrabajo',
      'trabajosRealizar',
      'operariosAsignados',
      'direccionObra',
      'personaContacto'
    ];

    console.log('🔍 Validando formulario...');
    console.log('📋 Datos actuales:', formData);

    for (const field of required) {
      const value = formData[field]?.trim();
      console.log(`- ${field}: "${value}" (${value ? '✅' : '❌'})`);
      
      if (!value) {
        const fieldNames = {
          fechaInicio: 'Fecha de inicio',
          fechaFin: 'Fecha de fin',
          codigoObra: 'Código de obra',
          partidaTrabajo: 'Partida de trabajo',
          trabajosRealizar: 'Trabajos a realizar',
          operariosAsignados: 'Operarios asignados',
          direccionObra: 'Dirección de obra',
          personaContacto: 'Persona de contacto'
        };
        showToast({ 
          type: "error", 
          message: `El campo "${fieldNames[field]}" es obligatorio` 
        });
        return false;
      }
    }

    // Validar que fecha fin no sea anterior a fecha inicio
    if (formData.fechaInicio && formData.fechaFin) {
      const inicio = new Date(formData.fechaInicio);
      const fin = new Date(formData.fechaFin);
      if (fin < inicio) {
        showToast({ 
          type: "error", 
          message: "La fecha de fin no puede ser anterior a la fecha de inicio" 
        });
        return false;
      }
    }

    // 🛡️ Validar PRL
    if (solicitarPRL && !responsablePRL) {
      showToast({ 
        type: "error", 
        message: "Debes seleccionar un responsable de PRL" 
      });
      return false;
    }

    // Validar teléfono si se proporciona
    if (formData.telefonoContacto) {
      console.log('📞 Validando teléfono:', formData.telefonoContacto);
      const phoneValid = isValidPhone(formData.telefonoContacto);
      console.log('📞 Teléfono válido:', phoneValid);
      
      if (!phoneValid) {
        showToast({ 
          type: "error", 
          message: "El formato del teléfono no es válido" 
        });
        return false;
      }
    }

    console.log('✅ Validación exitosa');
    return true;
  };

  // Enviar solicitud
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🚀 Iniciando envío de solicitud...');
    
    if (!validateForm()) {
      console.log('❌ Validación falló');
      return;
    }
    
    if (loading) {
      console.log('⏳ Ya está cargando, saliendo...');
      return;
    }

    try {
      console.log('🔄 Configurando loading...');
      setLoading(true);

      // 💾 GUARDAR datos del formulario ANTES de cualquier validación
      console.log('💾 Guardando datos del formulario en localStorage por seguridad...');
      localStorage.setItem('solicitudParteTrabajo_draft', JSON.stringify({
        formData,
        selectedOperarios,
        timestamp: new Date().toISOString()
      }));

      console.log('🆔 Generando ID único...');
      const requestId = uuid();
      console.log('✅ ID generado:', requestId);

      console.log('📋 Preparando datos de la solicitud...');
      
      // 🔥 HOTFIX: Asegurar que companyId NUNCA sea null
      let effectiveCompanyId = companyId;
      if (!effectiveCompanyId) {
        console.warn('⚠️ companyId es null, intentando refrescar token...');
        try {
          const currentUser = auth.currentUser;
          if (!currentUser) {
            showToast({ 
              type: "warning", 
              message: "⚠️ Tu sesión ha expirado. Por favor, vuelve a iniciar sesión." 
            });
            setLoading(false);
            setTimeout(() => navigate('/login'), 2000);
            return;
          }
          // Forzar refresco del token para obtener claims actualizados
          await currentUser.getIdToken(true);
          const tokenResult = await currentUser.getIdTokenResult();
          effectiveCompanyId = tokenResult.claims.companyId || null;
          console.log('🔄 companyId tras refresco:', effectiveCompanyId);
        } catch (refreshError) {
          console.error('❌ Error refrescando token:', refreshError);
        }

        if (!effectiveCompanyId) {
          showToast({ 
            type: "error", 
            message: "Error: No se pudo determinar la empresa. Cierra sesión, vuelve a entrar e inténtalo de nuevo." 
          });
          setLoading(false);
          return;
        }
      }
      
      const requestData = {
        id: requestId,
        requesterId: user?.uid || null,
        requesterName: user?.displayName || user?.email || "Usuario",
        requesterEmail: user?.email || "",
        companyId: effectiveCompanyId,
        
        // Datos del formulario (con fechas formateadas)
        ...formData,
        fechasActuacion: formatRangoFechas(), // Campo unificado para mostrar en historial/detalles
        rangoFechas: formatRangoFechas(),
        
        // 👥 Operarios seleccionados
        selectedOperarios: selectedOperarios || [],
        
        // 🛡️ Datos PRL
        solicitarPRL: solicitarPRL || false,
        ...(solicitarPRL && responsablePRL ? { responsablePRL: responsablePRL } : {}),
        prlEnlaceGenerado: solicitarPRL || false, // Flag para saber si se debe generar enlace
        
        // 💰 Datos Centro de Gastos
        solicitarCentroGastos: solicitarCentroGastos || false,
        
        // 🏨 Datos Hotel  
        solicitarHotel: solicitarHotel || false,
        
        // Metadatos
        status: "enviada",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      
      // 🧹 Limpiar campos undefined para evitar errores de Firestore
      Object.keys(requestData).forEach(key => {
        if (requestData[key] === undefined) {
          delete requestData[key];
        }
      });
      
      console.log('✅ Datos preparados:', requestData);

      console.log('💾 Guardando en Firestore...');
      await setDoc(doc(db, "work_requests", requestId), requestData);
      console.log('✅ Guardado en Firestore exitoso');

      console.log('📧 Preparando envío de email...');
      const SEND_URL = import.meta.env.VITE_SEND_EMAIL_URL?.trim();
      console.log('🔗 SEND_URL:', SEND_URL);
      console.log('👤 User:', user);
      
      if (SEND_URL && (user || auth.currentUser)) {
        try {
          console.log('🔑 Obteniendo token...');
          const currentUser = user || auth.currentUser;
          const idToken = await currentUser.getIdToken(true);
          console.log('✅ Token obtenido');
          
          console.log('📝 Construyendo email...');
          const emailHtml = buildWorkRequestEmail(requestData);
          console.log('✅ Email construido');
          
          console.log('📤 Enviando email...');
          const emailRes = await fetch(SEND_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              to: ["ptrabajo@cejm.eu", requestData.requesterEmail].filter(Boolean),
              subject: `Solicitud Parte de Trabajo - ${formData.codigoObra} - ${requestData.requesterName}`,
              html: emailHtml,
            }),
          });
          const emailData = await emailRes.json().catch(() => ({}));
          if (!emailRes.ok || !emailData.ok) {
            console.error("❌ Error en respuesta email:", emailData);
            
            // 🚨 NOTIFICACIÓN VISIBLE AL USUARIO
            showToast({
              type: "error",
              message: "⚠️ SOLICITUD GUARDADA pero el EMAIL NO SE ENVIÓ. Usa el botón 'Reenviar Email' en el historial.",
              duration: 8000 // 8 segundos para que lo vea bien
            });
            
            // Marcar en Firestore que el email falló
            await setDoc(doc(db, "work_requests", requestId), {
              emailSentAt: null,
              emailError: emailData.error || `HTTP ${emailRes.status}`,
              emailErrorAt: serverTimestamp(),
            }, { merge: true });
            
            throw new Error(emailData.error || `HTTP ${emailRes.status}`);
          }
          console.log('✅ Email enviado correctamente');

          // Actualizar timestamp de envío
          await setDoc(doc(db, "work_requests", requestId), {
            emailSentAt: serverTimestamp(),
            emailError: null, // Limpiar error si existía
          }, { merge: true });

          // 💰 ENVIAR CORREO DE CENTRO DE GASTOS si está solicitado
          if (solicitarCentroGastos) {
            console.log('💰 Enviando correo de centro de gastos a Angel Rivera...');
            try {
              const emailCentroGastos = buildCentroGastosEmail(requestData);
              const emailCGRes = await fetch(SEND_URL, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${idToken}`,
                },
                body: JSON.stringify({
                  to: "arivera@cejm.eu",
                  subject: `🔔 SOLICITUD CENTRO DE GASTOS - ${formData.codigoObra}`,
                  html: emailCentroGastos,
                }),
              });
              const emailCGData = await emailCGRes.json().catch(() => ({}));
              if (!emailCGRes.ok || !emailCGData.ok) {
                console.error("❌ Error enviando email centro de gastos:", emailCGData);
              } else {
                console.log('✅ Email centro de gastos enviado correctamente');
              }
            } catch (cgEmailError) {
              console.error("❌ Error enviando email centro de gastos:", cgEmailError);
            }
          }

          // 🏨 ENVIAR CORREO DE HOTEL si está solicitado
          if (solicitarHotel) {
            console.log('🏨 Enviando correo de hotel a mandres@cejm.eu y ddiaz@cejm.eu...');
            try {
              const emailHotel = buildHotelEmail(requestData);
              const emailHotelRes = await fetch(SEND_URL, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${idToken}`,
                },
                body: JSON.stringify({
                  to: ["mandres@cejm.eu", "ddiaz@cejm.eu"],
                  subject: `🏨 RESERVAR HOTEL - ${formData.codigoObra}`,
                  html: emailHotel,
                }),
              });
              const emailHotelData = await emailHotelRes.json().catch(() => ({}));
              if (!emailHotelRes.ok || !emailHotelData.ok) {
                console.error("❌ Error enviando email hotel:", emailHotelData);
              } else {
                console.log('✅ Email hotel enviado correctamente');
              }
            } catch (hotelEmailError) {
              console.error("❌ Error enviando email hotel:", hotelEmailError);
            }
          }

        } catch (emailError) {
          console.error("❌ Error enviando email:", emailError);
          
          // 🚨 NOTIFICACIÓN MUY VISIBLE
          showToast({ 
            type: "error", 
            message: "⚠️ SOLICITUD GUARDADA pero el EMAIL NO SE ENVIÓ. Podrás reenviarlo desde el historial.", 
            duration: 10000 // 10 segundos
          });
          
          // Marcar el error en Firestore
          try {
            await setDoc(doc(db, "work_requests", requestId), {
              emailSentAt: null,
              emailError: emailError.message || String(emailError),
              emailErrorAt: serverTimestamp(),
            }, { merge: true });
          } catch (e) {
            console.error("No se pudo marcar error de email:", e);
          }
        }
      }

      // Mensaje de éxito SOLO si llegamos hasta aquí
      showToast({ 
        type: "success", 
        message: "¡Solicitud guardada! Revisa el historial para verificar el envío del email." 
      });

      // 💾 Guardar valores en historial para próximas solicitudes
      saveToHistorial('Ofertas', formData.numeroOferta);
      saveToHistorial('Presupuestos', formData.numeroPresupuesto);
      saveToHistorial('Codigos', formData.codigoObra);
      saveToHistorial('Partidas', formData.partidaTrabajo);

      // Guardar datos para pre-rellenar el parte de trabajo
      localStorage.setItem('solicitudData', JSON.stringify({
        ...formData,
        requestId,
        requesterName: requestData.requesterName,
        selectedOperarios: selectedOperarios // 👥 Incluir operarios seleccionados
      }));

      // Mostrar botón para crear parte de trabajo
      setShowCreateWorkButton(true);

      // Limpiar el draft guardado ya que se envió exitosamente
      localStorage.removeItem('solicitudParteTrabajo_draft');

      // NO limpiar formulario para que puedan ver los datos

    } catch (error) {
      console.error("Error enviando solicitud:", error);
      showToast({ 
        type: "error", 
        message: `Error al enviar solicitud: ${error.message}` 
      });
    } finally {
      setLoading(false);
    }
  };

  // Construir email HTML
  const buildWorkRequestEmail = (data) => {
    const formatDate = (date) => {
      return new Date(date).toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    // URL para ir al historial de solicitudes
    const baseUrl = import.meta.env.VITE_BASE_URL || 'https://firma-digital-db89b.web.app';
    const historialUrl = `${baseUrl}/historial-solicitudes`;

    return `
      <div style="font-family:system-ui,Segoe UI,Roboto,Arial,sans-serif;font-size:15px;color:#111;max-width:600px;margin:0 auto;">
        <div style="background:#0b1028;padding:20px;border-radius:12px 12px 0 0;">
          <h1 style="color:#e9eefc;margin:0;font-size:24px;">🔧 SOLICITUD DE PARTE DE TRABAJO</h1>
        </div>
        
        <div style="background:#fff;padding:24px;border-radius:0 0 12px 12px;">
          <div style="background:#f0f9ff;padding:16px;border-radius:8px;margin-bottom:20px;border-left:4px solid #4f6cf7;">
            <p style="margin:0 0 8px;"><strong>Solicitado por:</strong> ${data.requesterName}</p>
            <p style="margin:0 0 8px;"><strong>Email:</strong> ${data.requesterEmail}</p>
            <p style="margin:0;"><strong>Fecha solicitud:</strong> ${formatDate(new Date())}</p>
          </div>

          <h3 style="color:#333;border-bottom:2px solid #eee;padding-bottom:8px;">DATOS DEL TRABAJO</h3>
          <table style="width:100%;margin-bottom:20px;">
            <tr><td style="padding:4px 0;"><strong>Fecha de inicio:</strong></td><td style="padding:4px 0;">${new Date(data.fechaInicio).toLocaleDateString('es-ES')}</td></tr>
            <tr><td style="padding:4px 0;"><strong>Fecha de fin:</strong></td><td style="padding:4px 0;">${new Date(data.fechaFin).toLocaleDateString('es-ES')}</td></tr>
            <tr><td style="padding:4px 0;"><strong>Rango de fechas:</strong></td><td style="padding:4px 0;">${data.rangoFechas}</td></tr>
            ${data.numeroOferta ? `<tr><td style="padding:4px 0;"><strong>Nº Oferta/Pedido:</strong></td><td style="padding:4px 0;">${data.numeroOferta}</td></tr>` : ''}
            ${data.numeroPresupuesto ? `<tr><td style="padding:4px 0;"><strong>Nº Presupuesto:</strong></td><td style="padding:4px 0;">${data.numeroPresupuesto}</td></tr>` : ''}
            <tr><td style="padding:4px 0;"><strong>Código de Obra:</strong></td><td style="padding:4px 0;">${data.codigoObra}</td></tr>
            <tr><td style="padding:4px 0;"><strong>Partida de Trabajo:</strong></td><td style="padding:4px 0;">${data.partidaTrabajo}</td></tr>
          </table>

          <h3 style="color:#333;border-bottom:2px solid #eee;padding-bottom:8px;">DESCRIPCIÓN</h3>
          <div style="background:#f9f9f9;padding:12px;border-radius:6px;margin-bottom:20px;">
            <p style="margin:0;white-space:pre-wrap;">${data.trabajosRealizar}</p>
          </div>

          <h3 style="color:#333;border-bottom:2px solid #eee;padding-bottom:8px;">RECURSOS</h3>
          <p><strong>Operarios Asignados:</strong> ${data.operariosAsignados}</p>
          
          ${data.selectedOperarios && data.selectedOperarios.length > 0 ? `
          <div style="background:#f0f9ff;padding:12px;border-radius:6px;margin:12px 0;border-left:4px solid #4f6cf7;">
            <h4 style="margin:0 0 8px 0;color:#333;">👥 Equipo Seleccionado (${data.selectedOperarios.length} operarios):</h4>
            <table style="width:100%;">
              ${data.selectedOperarios.map(op => `
                <tr>
                  <td style="padding:4px 0;"><strong>${op.nombre}</strong></td>
                  <td style="padding:4px 0;">${op.especialidad}</td>
                  <td style="padding:4px 0;"><a href="mailto:${op.email}">${op.email}</a></td>
                  <td style="padding:4px 0;">${op.telefono}</td>
                </tr>
              `).join('')}
            </table>
          </div>
          ` : ''}

          <h3 style="color:#333;border-bottom:2px solid #eee;padding-bottom:8px;">UBICACIÓN</h3>
          <table style="width:100%;margin-bottom:30px;">
            <tr><td style="padding:4px 0;"><strong>Dirección:</strong></td><td style="padding:4px 0;">${data.direccionObra}</td></tr>
            <tr><td style="padding:4px 0;"><strong>Persona de Contacto:</strong></td><td style="padding:4px 0;">${data.personaContacto}</td></tr>
            ${data.telefonoContacto ? `<tr><td style="padding:4px 0;"><strong>Teléfono:</strong></td><td style="padding:4px 0;">${data.telefonoContacto}</td></tr>` : ''}
          </table>

          <!-- ENLACE AL HISTORIAL DE SOLICITUDES -->
          <div style="background:#e8f5e8;padding:20px;border-radius:8px;margin:20px 0;border-left:4px solid #10b981;text-align:center;">
            <h3 style="color:#065f46;margin:0 0 12px 0;">📋 GESTIONAR SOLICITUDES</h3>
            <p style="margin:0 0 16px 0;color:#047857;">Revisa el historial de solicitudes para crear partes de trabajo cuando sea necesario:</p>
            
            <a href="${historialUrl}" 
               target="_blank"
               style="display:inline-block;background:#10b981;color:#fff;text-decoration:none;
                      padding:14px 28px;border-radius:8px;font-weight:600;font-size:16px;
                      box-shadow:0 2px 4px rgba(16,185,129,0.3);">
              📋 Ver Historial de Solicitudes
            </a>
            
            <p style="margin:16px 0 0 0;font-size:12px;color:#6b7280;">
              Desde el historial podrás crear partes de trabajo con datos pre-rellenados.
            </p>
          </div>

          ${data.solicitarPRL ? `
          <!-- SECCIÓN PRL -->
          <div style="background:#fef3c7;padding:20px;border-radius:8px;margin:20px 0;border-left:4px solid #f59e0b;">
            <h3 style="color:#92400e;margin:0 0 12px 0;">🛡️ PREVENCIÓN DE RIESGOS LABORALES (PRL)</h3>
            <p style="margin:0 0 8px 0;color:#78350f;">
              <strong>✅ Se ha solicitado evaluación de PRL para esta obra</strong>
            </p>
            <p style="margin:0 0 16px 0;color:#78350f;">
              <strong>Responsable PRL:</strong> ${data.responsablePRL?.nombre} (${data.responsablePRL?.email})
            </p>
            
            <div style="text-align:center;">
              <a href="${baseUrl}/prl/${data.id}" 
                 target="_blank"
                 style="display:inline-block;background:#f59e0b;color:#fff;text-decoration:none;
                        padding:14px 28px;border-radius:8px;font-weight:600;font-size:16px;
                        box-shadow:0 2px 4px rgba(245,158,11,0.3);">
                🛡️ Completar Formulario PRL
              </a>
            </div>
            
            <p style="margin:16px 0 0 0;font-size:12px;color:#92400e;text-align:center;">
              El responsable PRL debe completar este formulario antes de iniciar los trabajos.
            </p>
          </div>
          ` : ''}
          <hr style="border:none;border-top:1px solid #eee;margin:24px 0"/>
          
          <p style="color:#999;font-size:12px;margin-bottom:0;">
            Sistema de Gestión CEJM - Solicitud generada automáticamente
          </p>
        </div>
      </div>
    `;
  };

  // Construir email para solicitud de centro de gastos
  const buildCentroGastosEmail = (data) => {
    const formatDate = (date) => {
      return new Date(date).toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    return `
      <div style="font-family:system-ui,Segoe UI,Roboto,Arial,sans-serif;font-size:15px;color:#111;max-width:650px;margin:0 auto;">
        <div style="background:#0284c7;padding:24px;border-radius:12px 12px 0 0;">
          <h1 style="color:#fff;margin:0;font-size:26px;">💰 SOLICITUD DE CENTRO DE GASTOS</h1>
        </div>
        
        <div style="background:#fff;padding:28px;border-radius:0 0 12px 12px;box-shadow:0 4px 6px rgba(0,0,0,0.1);">
          <div style="background:#e0f2fe;padding:18px;border-radius:8px;margin-bottom:24px;border-left:4px solid #0284c7;">
            <h3 style="margin:0 0 12px 0;color:#075985;font-size:18px;">📋 Información de la Solicitud</h3>
            <p style="margin:0 0 8px;"><strong style="color:#075985;">Solicitado por:</strong> ${data.requesterName}</p>
            <p style="margin:0 0 8px;"><strong style="color:#075985;">Email:</strong> ${data.requesterEmail}</p>
            <p style="margin:0;"><strong style="color:#075985;">Fecha solicitud:</strong> ${formatDate(new Date())}</p>
          </div>

          <div style="background:#fef3c7;padding:20px;border-radius:8px;margin-bottom:24px;border:2px solid #fbbf24;">
            <h3 style="margin:0 0 16px 0;color:#92400e;font-size:20px;">⚠️ Acción Requerida</h3>
            <p style="margin:0;color:#78350f;font-size:16px;line-height:1.6;">
              Se solicita la <strong>creación de un centro de gastos</strong> para la siguiente obra:
            </p>
          </div>

          <h3 style="color:#075985;border-bottom:2px solid #bae6fd;padding-bottom:10px;margin:24px 0 16px 0;">🏗️ DATOS DE LA OBRA</h3>
          <table style="width:100%;margin-bottom:24px;border-collapse:collapse;">
            <tr style="background:#f0f9ff;">
              <td style="padding:10px;border:1px solid #bae6fd;"><strong>Código de Obra:</strong></td>
              <td style="padding:10px;border:1px solid #bae6fd;font-weight:600;color:#0284c7;">${data.codigoObra}</td>
            </tr>
            <tr>
              <td style="padding:10px;border:1px solid #bae6fd;"><strong>Partida de Trabajo:</strong></td>
              <td style="padding:10px;border:1px solid #bae6fd;">${data.partidaTrabajo}</td>
            </tr>
            <tr style="background:#f0f9ff;">
              <td style="padding:10px;border:1px solid #bae6fd;"><strong>Fecha de inicio:</strong></td>
              <td style="padding:10px;border:1px solid #bae6fd;">${new Date(data.fechaInicio).toLocaleDateString('es-ES')}</td>
            </tr>
            <tr>
              <td style="padding:10px;border:1px solid #bae6fd;"><strong>Fecha de fin:</strong></td>
              <td style="padding:10px;border:1px solid #bae6fd;">${new Date(data.fechaFin).toLocaleDateString('es-ES')}</td>
            </tr>
            <tr style="background:#f0f9ff;">
              <td style="padding:10px;border:1px solid #bae6fd;"><strong>Rango de fechas:</strong></td>
              <td style="padding:10px;border:1px solid #bae6fd;font-weight:600;">${data.rangoFechas}</td>
            </tr>
            ${data.numeroOferta ? `
            <tr>
              <td style="padding:10px;border:1px solid #bae6fd;"><strong>Nº Oferta/Pedido:</strong></td>
              <td style="padding:10px;border:1px solid #bae6fd;">${data.numeroOferta}</td>
            </tr>
            ` : ''}
            ${data.numeroPresupuesto ? `
            <tr style="background:#f0f9ff;">
              <td style="padding:10px;border:1px solid #bae6fd;"><strong>Nº Presupuesto:</strong></td>
              <td style="padding:10px;border:1px solid #bae6fd;">${data.numeroPresupuesto}</td>
            </tr>
            ` : ''}
          </table>

          <h3 style="color:#075985;border-bottom:2px solid #bae6fd;padding-bottom:10px;margin:24px 0 16px 0;">📝 DESCRIPCIÓN DE TRABAJOS</h3>
          <div style="background:#f9fafb;padding:16px;border-radius:8px;margin-bottom:24px;border:1px solid #d1d5db;">
            <p style="margin:0;white-space:pre-wrap;line-height:1.6;">${data.trabajosRealizar}</p>
          </div>

          <h3 style="color:#075985;border-bottom:2px solid #bae6fd;padding-bottom:10px;margin:24px 0 16px 0;">👥 RECURSOS ASIGNADOS</h3>
          <div style="background:#f0f9ff;padding:16px;border-radius:8px;margin-bottom:24px;border-left:4px solid #0284c7;">
            <p style="margin:0 0 8px;"><strong>Operarios Asignados:</strong> ${data.operariosAsignados}</p>
            ${data.selectedOperarios && data.selectedOperarios.length > 0 ? `
              <p style="margin:12px 0 8px;font-weight:600;color:#075985;">Equipo Seleccionado (${data.selectedOperarios.length} operarios):</p>
              <ul style="margin:0;padding-left:20px;">
                ${data.selectedOperarios.map(op => `
                  <li style="margin:4px 0;"><strong>${op.nombre}</strong> - ${op.email}</li>
                `).join('')}
              </ul>
            ` : ''}
          </div>

          <h3 style="color:#075985;border-bottom:2px solid #bae6fd;padding-bottom:10px;margin:24px 0 16px 0;">📍 UBICACIÓN Y CONTACTO</h3>
          <table style="width:100%;margin-bottom:24px;border-collapse:collapse;">
            <tr style="background:#f0f9ff;">
              <td style="padding:10px;border:1px solid #bae6fd;"><strong>Dirección de Obra:</strong></td>
              <td style="padding:10px;border:1px solid #bae6fd;">${data.direccionObra}</td>
            </tr>
            <tr>
              <td style="padding:10px;border:1px solid #bae6fd;"><strong>Persona de Contacto:</strong></td>
              <td style="padding:10px;border:1px solid #bae6fd;">${data.personaContacto}</td>
            </tr>
            ${data.telefonoContacto ? `
            <tr style="background:#f0f9ff;">
              <td style="padding:10px;border:1px solid #bae6fd;"><strong>Teléfono de Contacto:</strong></td>
              <td style="padding:10px;border:1px solid #bae6fd;">${data.telefonoContacto}</td>
            </tr>
            ` : ''}
            ${data.emailCliente ? `
            <tr>
              <td style="padding:10px;border:1px solid #bae6fd;"><strong>Email Cliente:</strong></td>
              <td style="padding:10px;border:1px solid #bae6fd;"><a href="mailto:${data.emailCliente}" style="color:#0284c7;">${data.emailCliente}</a></td>
            </tr>
            ` : ''}
          </table>

          <div style="background:#dcfce7;padding:20px;border-radius:8px;margin:24px 0;border-left:4px solid #10b981;text-align:center;">
            <h3 style="color:#065f46;margin:0 0 8px 0;">✅ Próximos Pasos</h3>
            <p style="margin:0;color:#047857;font-size:14px;">
              Por favor, procede con la creación del centro de gastos en el sistema contable con los datos proporcionados.
            </p>
          </div>

          <hr style="border:none;border-top:1px solid #e5e7eb;margin:24px 0"/>
          
          <p style="color:#6b7280;font-size:13px;margin:0;text-align:center;">
            Sistema de Gestión CEJM - Solicitud generada automáticamente<br/>
            <span style="font-size:11px;">Este correo se envía cuando se solicita la creación de un centro de gastos para una obra</span>
          </p>
        </div>
      </div>
    `;
  };

  // Construir email para solicitud de hotel
  const buildHotelEmail = (data) => {
    const formatDate = (date) => {
      return new Date(date).toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    return `
      <div style="font-family:system-ui,Segoe UI,Roboto,Arial,sans-serif;font-size:15px;color:#111;max-width:650px;margin:0 auto;">
        <div style="background:#7c3aed;padding:24px;border-radius:12px 12px 0 0;">
          <h1 style="color:#fff;margin:0;font-size:26px;">🏨 SOLICITUD DE HOTEL</h1>
        </div>
        
        <div style="background:#fff;padding:28px;border-radius:0 0 12px 12px;box-shadow:0 4px 6px rgba(0,0,0,0.1);">
          <div style="background:#f3e8ff;padding:18px;border-radius:8px;margin-bottom:24px;border-left:4px solid #7c3aed;">
            <h3 style="margin:0 0 12px 0;color:#5b21b6;font-size:18px;">📋 Información de la Solicitud</h3>
            <p style="margin:0 0 8px;"><strong style="color:#5b21b6;">Solicitado por:</strong> ${data.requesterName}</p>
            <p style="margin:0 0 8px;"><strong style="color:#5b21b6;">Email:</strong> ${data.requesterEmail}</p>
            <p style="margin:0;"><strong style="color:#5b21b6;">Fecha solicitud:</strong> ${formatDate(new Date())}</p>
          </div>

          <div style="background:#fef3c7;padding:20px;border-radius:8px;margin-bottom:24px;border:2px solid #fbbf24;">
            <h3 style="margin:0 0 16px 0;color:#92400e;font-size:20px;">⚠️ Acción Requerida</h3>
            <p style="margin:0;color:#78350f;font-size:16px;line-height:1.6;">
              Se solicita <strong>buscar hotel</strong> para el siguiente parte de trabajo:
            </p>
          </div>

          <h3 style="color:#5b21b6;border-bottom:2px solid #ddd6fe;padding-bottom:10px;margin:24px 0 16px 0;">🏗️ DATOS DE LA OBRA</h3>
          <table style="width:100%;margin-bottom:24px;border-collapse:collapse;">
            <tr style="background:#f9fafb;">
              <td style="padding:10px;border:1px solid #ddd6fe;"><strong>Código de Obra:</strong></td>
              <td style="padding:10px;border:1px solid #ddd6fe;font-weight:600;color:#7c3aed;">${data.codigoObra}</td>
            </tr>
            <tr>
              <td style="padding:10px;border:1px solid #ddd6fe;"><strong>Partida de Trabajo:</strong></td>
              <td style="padding:10px;border:1px solid #ddd6fe;">${data.partidaTrabajo}</td>
            </tr>
            <tr style="background:#f9fafb;">
              <td style="padding:10px;border:1px solid #ddd6fe;"><strong>Fecha de inicio:</strong></td>
              <td style="padding:10px;border:1px solid #ddd6fe;">${new Date(data.fechaInicio).toLocaleDateString('es-ES')}</td>
            </tr>
            <tr>
              <td style="padding:10px;border:1px solid #ddd6fe;"><strong>Fecha de fin:</strong></td>
              <td style="padding:10px;border:1px solid #ddd6fe;">${new Date(data.fechaFin).toLocaleDateString('es-ES')}</td>
            </tr>
            <tr style="background:#f9fafb;">
              <td style="padding:10px;border:1px solid #ddd6fe;"><strong>Rango de fechas:</strong></td>
              <td style="padding:10px;border:1px solid #ddd6fe;font-weight:600;">${data.rangoFechas}</td>
            </tr>
            ${data.numeroOferta ? `
            <tr>
              <td style="padding:10px;border:1px solid #ddd6fe;"><strong>Nº Oferta/Pedido:</strong></td>
              <td style="padding:10px;border:1px solid #ddd6fe;">${data.numeroOferta}</td>
            </tr>
            ` : ''}
            ${data.numeroPresupuesto ? `
            <tr style="background:#f9fafb;">
              <td style="padding:10px;border:1px solid #ddd6fe;"><strong>Nº Presupuesto:</strong></td>
              <td style="padding:10px;border:1px solid #ddd6fe;">${data.numeroPresupuesto}</td>
            </tr>
            ` : ''}
          </table>

          <h3 style="color:#5b21b6;border-bottom:2px solid #ddd6fe;padding-bottom:10px;margin:24px 0 16px 0;">📝 DESCRIPCIÓN DE TRABAJOS</h3>
          <div style="background:#f9fafb;padding:16px;border-radius:8px;margin-bottom:24px;border:1px solid #d1d5db;">
            <p style="margin:0;white-space:pre-wrap;line-height:1.6;">${data.trabajosRealizar}</p>
          </div>

          <h3 style="color:#5b21b6;border-bottom:2px solid #ddd6fe;padding-bottom:10px;margin:24px 0 16px 0;">👥 RECURSOS ASIGNADOS</h3>
          <div style="background:#f3e8ff;padding:16px;border-radius:8px;margin-bottom:24px;border-left:4px solid #7c3aed;">
            <p style="margin:0 0 8px;"><strong>Operarios Asignados:</strong> ${data.operariosAsignados}</p>
            ${data.selectedOperarios && data.selectedOperarios.length > 0 ? `
              <p style="margin:12px 0 8px;font-weight:600;color:#5b21b6;">Equipo Seleccionado (${data.selectedOperarios.length} operarios):</p>
              <ul style="margin:0;padding-left:20px;">
                ${data.selectedOperarios.map(op => `
                  <li style="margin:4px 0;"><strong>${op.nombre}</strong> - ${op.email}</li>
                `).join('')}
              </ul>
            ` : ''}
          </div>

          <h3 style="color:#5b21b6;border-bottom:2px solid #ddd6fe;padding-bottom:10px;margin:24px 0 16px 0;">📍 UBICACIÓN Y CONTACTO</h3>
          <table style="width:100%;margin-bottom:24px;border-collapse:collapse;">
            <tr style="background:#f9fafb;">
              <td style="padding:10px;border:1px solid #ddd6fe;"><strong>Dirección de Obra:</strong></td>
              <td style="padding:10px;border:1px solid #ddd6fe;">${data.direccionObra}</td>
            </tr>
            <tr>
              <td style="padding:10px;border:1px solid #ddd6fe;"><strong>Persona de Contacto:</strong></td>
              <td style="padding:10px;border:1px solid #ddd6fe;">${data.personaContacto}</td>
            </tr>
            ${data.telefonoContacto ? `
            <tr style="background:#f9fafb;">
              <td style="padding:10px;border:1px solid #ddd6fe;"><strong>Teléfono de Contacto:</strong></td>
              <td style="padding:10px;border:1px solid #ddd6fe;">${data.telefonoContacto}</td>
            </tr>
            ` : ''}
            ${data.emailCliente ? `
            <tr>
              <td style="padding:10px;border:1px solid #ddd6fe;"><strong>Email Cliente:</strong></td>
              <td style="padding:10px;border:1px solid #ddd6fe;"><a href="mailto:${data.emailCliente}" style="color:#7c3aed;">${data.emailCliente}</a></td>
            </tr>
            ` : ''}
          </table>

          <div style="background:#dcfce7;padding:20px;border-radius:8px;margin:24px 0;border-left:4px solid #10b981;text-align:center;">
            <h3 style="color:#065f46;margin:0 0 8px 0;">✅ Próximos Pasos</h3>
            <p style="margin:0;color:#047857;font-size:14px;">
              Por favor, buscar hotel para los operarios en las fechas indicadas cerca de la dirección de obra proporcionada.
            </p>
          </div>

          <hr style="border:none;border-top:1px solid #e5e7eb;margin:24px 0"/>
          
          <p style="color:#6b7280;font-size:13px;margin:0;text-align:center;">
            Sistema de Gestión CEJM - Solicitud generada automáticamente<br/>
            <span style="font-size:11px;">Este correo se envía cuando se solicita la búsqueda de hotel para una obra</span>
          </p>
        </div>
      </div>
    `;
  };

  // Ir a crear parte de trabajo con datos pre-rellenados
  const goToCreateWorkPart = () => {
    // Los datos ya están guardados en localStorage
    navigate('/upload?from=solicitud');
  };

  // Limpiar formulario y empezar de nuevo
  const startNewRequest = () => {
    setFormData({
      fechaInicio: "",
      fechaFin: "",
      numeroOferta: "",
      numeroPresupuesto: "",
      codigoObra: "",
      partidaTrabajo: "",
      trabajosRealizar: "",
      operariosAsignados: "",
      direccionObra: "",
      personaContacto: "",
      telefonoContacto: "",
    });
    setSelectedOperarios([]); // 👥 Limpiar operarios seleccionados
    setFiltroOperarios(""); // 🔍 Limpiar filtro
    setSolicitarPRL(false); // 🛡️ Reset PRL
    setResponsablePRL(null);
    setSolicitarCentroGastos(false); // 💰 Reset Centro de Gastos
    setShowCreateWorkButton(false);
    localStorage.removeItem('solicitudData');
  };

  return (
    <div className="container">
      <div className="header">
        <span className="badge">FIRMAS CEJM</span>
      </div>

      <h1 className="title">🔧 Solicitar Parte de Trabajo</h1>

      <div className="card">
        <form onSubmit={handleSubmit} className="grid">
          {/* Información del técnico */}
          <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
            <p className="kicker" style={{ margin: '0 0 4px 0' }}>Técnico solicitante</p>
            <p style={{ margin: 0, fontWeight: 'bold', color: '#e9eefc' }}>
              {user?.displayName || user?.email?.split('@')[0] || "Usuario"}
            </p>
          </div>

          {/* Datos básicos - Rango de fechas */}
          <div>
            <p className="kicker" style={{ marginBottom: 8 }}>📅 Rango de fechas del trabajo</p>
            <div className="form-grid-2" style={{ marginBottom: 8 }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: 4 }}>
                  Fecha de inicio *
                </label>
                <DatePicker
                  value={formData.fechaInicio}
                  onChange={(v) => handleChange('fechaInicio', v)}
                  placeholder="Fecha de inicio"
                  max={formData.fechaFin || undefined}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: 4 }}>
                  Fecha de fin *
                </label>
                <DatePicker
                  value={formData.fechaFin}
                  onChange={(v) => handleChange('fechaFin', v)}
                  placeholder="Fecha de fin"
                  min={formData.fechaInicio || undefined}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="copy"
                onClick={setRangoHoy}
                style={{ padding: '4px 8px', fontSize: '11px' }}
              >
                Hoy
              </button>
              <button
                type="button"
                className="copy"
                onClick={setRangoEstaSemana}
                style={{ padding: '4px 8px', fontSize: '11px' }}
              >
                Esta semana
              </button>
              <button
                type="button"
                className="copy"
                onClick={setRangoProximaSemana}
                style={{ padding: '4px 8px', fontSize: '11px' }}
              >
                Próxima semana
              </button>
            </div>
            {formData.fechaInicio && formData.fechaFin && (
              <p className="meta" style={{ marginTop: 8, fontSize: 12, color: '#10b981' }}>
                ✅ Rango: {formatRangoFechas()}
              </p>
            )}
          </div>

          <div className="form-grid-2">
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: 4 }}>
                Nº Oferta/Pedido
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  list="ofertas-list"
                  placeholder="Nº Oferta/Pedido"
                  value={formData.numeroOferta}
                  onChange={(e) => handleChange('numeroOferta', e.target.value)}
                  onBlur={(e) => saveToHistorial('Ofertas', e.target.value)}
                />
                <datalist id="ofertas-list">
                  <option value="No aplica">No aplica</option>
                  {historialOfertas.map((oferta, idx) => (
                    <option key={idx} value={oferta}>{oferta}</option>
                  ))}
                </datalist>
              </div>
            </div>
          </div>

          <div className="form-grid-2">
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: 4 }}>
                Nº Presupuesto
              </label>
              <input
                list="presupuestos-list"
                placeholder="Nº Presupuesto"
                value={formData.numeroPresupuesto}
                onChange={(e) => handleChange('numeroPresupuesto', e.target.value)}
                onBlur={(e) => saveToHistorial('Presupuestos', e.target.value)}
              />
              <datalist id="presupuestos-list">
                <option value="No aplica">No aplica</option>
                {historialPresupuestos.map((pres, idx) => (
                  <option key={idx} value={pres}>{pres}</option>
                ))}
              </datalist>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: 4 }}>
                Código de Obra *
              </label>
              <input
                list="codigos-list"
                placeholder="Código de Obra *"
                value={formData.codigoObra}
                onChange={(e) => handleChange('codigoObra', e.target.value)}
                onBlur={(e) => saveToHistorial('Codigos', e.target.value)}
                required
              />
              <datalist id="codigos-list">
                <option value="No aplica">No aplica</option>
                {historialCodigos.map((codigo, idx) => (
                  <option key={idx} value={codigo}>{codigo}</option>
                ))}
              </datalist>
            </div>
          </div>

          {/* Partida de trabajo */}
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: 4 }}>
              Partida de Trabajo *
            </label>
            <input
              list="partidas-list"
              placeholder="Partida de Trabajo *"
              value={formData.partidaTrabajo}
              onChange={(e) => handleChange('partidaTrabajo', e.target.value)}
              onBlur={(e) => saveToHistorial('Partidas', e.target.value)}
              required
            />
            <datalist id="partidas-list">
              <option value="No aplica">No aplica</option>
              {historialPartidas.map((partida, idx) => (
                <option key={idx} value={partida}>{partida}</option>
              ))}
            </datalist>
          </div>

          {/* Descripción de trabajos */}
          <textarea
            placeholder="Trabajos a Realizar *"
            value={formData.trabajosRealizar}
            onChange={(e) => handleChange('trabajosRealizar', e.target.value)}
            rows={4}
            required
          />

          {/* Operarios */}
          <div className="grid">
            <p className="kicker">👥 Seleccionar Operarios</p>
            
            {/* Campo de búsqueda/filtro */}
            <div style={{ marginBottom: 12 }}>
              <input
                placeholder="🔍 Buscar operario por nombre..."
                value={filtroOperarios}
                onChange={(e) => setFiltroOperarios(e.target.value)}
              />
              <p className="meta" style={{ marginTop: 4 }}>
                {filtroOperarios ? 
                  `🔍 Mostrando ${operariosFiltrados.length} de ${operarios.length} operarios` :
                  `📋 ${operarios.length} operarios disponibles`
                }
              </p>
            </div>
            
            {/* Selector de operarios */}
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
              <select
                onChange={(e) => {
                  const operarioId = parseInt(e.target.value);
                  const operario = operarios.find(op => op.id === operarioId);
                  addOperario(operario);
                  e.target.value = "";
                  setFiltroOperarios(""); // Limpiar filtro después de seleccionar
                }}
                defaultValue=""
                disabled={operariosLoading}
              >
                <option value="" disabled>
                  {operariosLoading ? "Cargando operarios…" : 
                   filtroOperarios ? `Seleccionar de ${operariosFiltrados.length} filtrados...` :
                   "Seleccionar operario del equipo…"}
                </option>
                {operariosFiltrados.map(operario => (
                  <option key={operario.id} value={operario.id}>
                    {operario.nombre}
                  </option>
                ))}
              </select>
              
              {operariosLoading && (
                <span className="meta">⏳ Cargando...</span>
              )}
              
              {filtroOperarios && (
                <button 
                  type="button"
                  className="copy" 
                  onClick={() => setFiltroOperarios("")}
                  style={{ padding: '8px 12px', fontSize: '12px' }}
                >
                  ✕ Limpiar
                </button>
              )}
            </div>

            {/* Operarios seleccionados */}
            {selectedOperarios.length > 0 && (
              <div style={{ 
                background: '#f8fafc', 
                padding: '12px', 
                borderRadius: '8px', 
                border: '1px solid #e2e8f0',
                marginBottom: 12
              }}>
                <p className="meta" style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
                  Equipo seleccionado ({selectedOperarios.length} operarios):
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {selectedOperarios.map(operario => (
                    <span 
                      key={operario.id} 
                      className="badge" 
                      style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: 8,
                        background: '#dbeafe',
                        color: '#1e40af',
                        padding: '6px 12px'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{operario.nombre}</div>
                        <div className="meta" style={{ fontSize: '11px' }}>
                          {operario.email}
                        </div>
                      </div>
                      <button 
                        className="copy" 
                        onClick={() => removeOperario(operario.id)}
                        style={{ padding: '2px 6px', fontSize: '11px' }}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Campo manual de operarios (se auto-completa) */}
            <input
              placeholder="Operarios Asignados *"
              value={formData.operariosAsignados}
              onChange={(e) => handleChange('operariosAsignados', e.target.value)}
              required
              style={{
                borderColor: selectedOperarios.length > 0 ? '#10b981' : '#4f6cf7',
                color: selectedOperarios.length > 0 ? '#10b981' : '#0f172a'
              }}
            />
            <p className="meta" style={{ marginTop: 4 }}>
              {selectedOperarios.length > 0 ? 
                "✅ Campo completado automáticamente desde la selección de operarios" :
                "💡 Selecciona operarios arriba o escribe manualmente"
              }
            </p>
          </div>

          {/* Dirección */}
          <textarea
            placeholder="Dirección de la Obra *"
            value={formData.direccionObra}
            onChange={(e) => handleChange('direccionObra', e.target.value)}
            rows={2}
            required
          />

          {/* Contacto */}
          <div className="form-grid-2">
            <input
              placeholder="Persona de Contacto *"
              value={formData.personaContacto}
              onChange={(e) => handleChange('personaContacto', e.target.value)}
              required
            />
            <input
              placeholder="Teléfono de Contacto"
              value={formData.telefonoContacto}
              onChange={(e) => handleChange('telefonoContacto', e.target.value)}
              type="tel"
            />
          </div>

          {/* Email cliente */}
          <div>
            <input
              type="email"
              placeholder="📧 Correo del cliente (recibirá el parte firmado)"
              value={formData.emailCliente}
              onChange={(e) => handleChange('emailCliente', e.target.value)}
            />
          </div>

          {/* 🛡️ SECCIÓN PRL */}
          <div style={{ 
            background: '#fef3c7', 
            padding: '20px', 
            borderRadius: '10px', 
            border: '2px solid #f59e0b',
            marginTop: '20px'
          }}>
            <h3 style={{ color: '#92400e', marginBottom: '16px', fontSize: '18px' }}>
              🛡️ Prevención de Riesgos Laborales (PRL)
            </h3>
            
            {/* ¿Solicitar PRL? */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#78350f', marginBottom: '8px' }}>
                ¿Solicitar PRL? *
              </label>
              <div style={{ display: 'flex', gap: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <div 
                    onClick={() => setSolicitarPRL(true)}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      border: '2px solid #f59e0b',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: solicitarPRL === true ? '#f59e0b' : '#fff',
                      transition: 'all 0.15s',
                      flexShrink: 0
                    }}
                  >
                    {solicitarPRL === true && (
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff' }} />
                    )}
                  </div>
                  <span style={{ color: '#78350f', userSelect: 'none' }}>Sí</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <div 
                    onClick={() => setSolicitarPRL(false)}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      border: '2px solid #f59e0b',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: solicitarPRL === false ? '#f59e0b' : '#fff',
                      transition: 'all 0.15s',
                      flexShrink: 0
                    }}
                  >
                    {solicitarPRL === false && (
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff' }} />
                    )}
                  </div>
                  <span style={{ color: '#78350f', userSelect: 'none' }}>No</span>
                </label>
              </div>
            </div>

            {/* Responsable PRL (solo si solicitarPRL es true) */}
            {solicitarPRL && (
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#78350f', marginBottom: '8px' }}>
                  Responsable PRL *
                </label>
                
                {responsablePRL && (
                  <div style={{ 
                    background: '#dcfce7', 
                    padding: '12px', 
                    borderRadius: '6px', 
                    marginBottom: '12px',
                    border: '1px solid #10b981'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong style={{ color: '#065f46' }}>{responsablePRL.nombre}</strong>
                        <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#047857' }}>
                          {responsablePRL.email}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setResponsablePRL(null)}
                        style={{ 
                          background: '#ef4444', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '4px', 
                          padding: '6px 12px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Quitar
                      </button>
                    </div>
                  </div>
                )}

                {!responsablePRL && (
                  <>
                    <input
                      type="text"
                      placeholder="🔍 Buscar responsable PRL..."
                      value={filtroPRL}
                      onChange={(e) => setFiltroPRL(e.target.value)}
                      style={{ marginBottom: '12px' }}
                    />
                    
                    <div style={{ 
                      maxHeight: '200px', 
                      overflowY: 'auto', 
                      background: 'white', 
                      borderRadius: '6px', 
                      border: '1px solid #d1d5db' 
                    }}>
                      {operariosLoading ? (
                        <p style={{ padding: '12px', textAlign: 'center', color: '#6b7280' }}>Cargando operarios...</p>
                      ) : (
                        operarios
                          .filter(op => norm(op.nombre).includes(norm(filtroPRL)))
                          .map(operario => (
                            <div
                              key={operario.id}
                              onClick={() => {
                                setResponsablePRL(operario);
                                setFiltroPRL('');
                              }}
                              style={{
                                padding: '12px',
                                borderBottom: '1px solid #e5e7eb',
                                cursor: 'pointer',
                                transition: 'background 0.2s'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                            >
                              <strong style={{ color: '#111' }}>{operario.nombre}</strong>
                              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                                {operario.email}
                              </p>
                            </div>
                          ))
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* 💰 SECCIÓN CENTRO DE GASTOS */}
          <div style={{ 
            background: '#e0f2fe', 
            padding: '20px', 
            borderRadius: '10px', 
            border: '2px solid #0284c7',
            marginTop: '20px'
          }}>
            <h3 style={{ color: '#075985', marginBottom: '16px', fontSize: '18px' }}>
              💰 Centro de Gastos
            </h3>
            
            {/* ¿Solicitar Centro de Gastos? */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#075985', marginBottom: '8px' }}>
                ¿Solicitar creación de centro de gastos para la obra? *
              </label>
              <div style={{ display: 'flex', gap: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <div 
                    onClick={() => setSolicitarCentroGastos(true)}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      border: '2px solid #0284c7',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: solicitarCentroGastos === true ? '#0284c7' : '#fff',
                      transition: 'all 0.15s',
                      flexShrink: 0
                    }}
                  >
                    {solicitarCentroGastos === true && (
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff' }} />
                    )}
                  </div>
                  <span style={{ color: '#075985', userSelect: 'none' }}>Sí</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <div 
                    onClick={() => setSolicitarCentroGastos(false)}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      border: '2px solid #0284c7',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: solicitarCentroGastos === false ? '#0284c7' : '#fff',
                      transition: 'all 0.15s',
                      flexShrink: 0
                    }}
                  >
                    {solicitarCentroGastos === false && (
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff' }} />
                    )}
                  </div>
                  <span style={{ color: '#075985', userSelect: 'none' }}>No</span>
                </label>
              </div>
            </div>
            
            {solicitarCentroGastos && (
              <div style={{
                background: '#bae6fd',
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #0284c7'
              }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#075985' }}>
                  📧 Al enviar la solicitud, se notificará automáticamente a <strong>ANGEL ANTONIO RIVERA RODRIGUEZ</strong> para la creación del centro de gastos con todos los datos de la obra.
                </p>
              </div>
            )}
          </div>

          {/* 🏨 SECCIÓN HOTEL */}
          <div style={{ 
            background: '#f3e8ff', 
            padding: '20px', 
            borderRadius: '10px', 
            border: '2px solid #7c3aed',
            marginTop: '20px'
          }}>
            <h3 style={{ color: '#5b21b6', marginBottom: '16px', fontSize: '18px' }}>
              🏨 Hotel
            </h3>
            
            {/* ¿Se necesita Hotel? */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#5b21b6', marginBottom: '8px' }}>
                ¿Se necesita hotel? *
              </label>
              <div style={{ display: 'flex', gap: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <div 
                    onClick={() => setSolicitarHotel(true)}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      border: '2px solid #7c3aed',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: solicitarHotel === true ? '#7c3aed' : '#fff',
                      transition: 'all 0.15s',
                      flexShrink: 0
                    }}
                  >
                    {solicitarHotel === true && (
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff' }} />
                    )}
                  </div>
                  <span style={{ color: '#5b21b6', userSelect: 'none' }}>Sí</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <div 
                    onClick={() => setSolicitarHotel(false)}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      border: '2px solid #7c3aed',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: solicitarHotel === false ? '#7c3aed' : '#fff',
                      transition: 'all 0.15s',
                      flexShrink: 0
                    }}
                  >
                    {solicitarHotel === false && (
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff' }} />
                    )}
                  </div>
                  <span style={{ color: '#5b21b6', userSelect: 'none' }}>No</span>
                </label>
              </div>
            </div>
            
            {solicitarHotel && (
              <div style={{
                background: '#ddd6fe',
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #7c3aed'
              }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#5b21b6' }}>
                  📧 Al enviar la solicitud, se notificará automáticamente a <strong>mandres@cejm.eu</strong> y <strong>ddiaz@cejm.eu</strong> para que busquen hotel para este parte de trabajo en esta obra.
                </p>
              </div>
            )}
          </div>

          {/* Botones */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            {!showCreateWorkButton ? (
              <>
                <button 
                  type="submit" 
                  className="btn" 
                  disabled={loading}
                >
                  {loading ? "Enviando solicitud..." : "Enviar Solicitud"}
                </button>
                
                <button 
                  type="button" 
                  className="copy" 
                  onClick={() => navigate('/historial-solicitudes')}
                >
                  Ver Historial
                </button>
              </>
            ) : (
              <>
                <div style={{ 
                  background: '#f0fdf4', 
                  padding: '16px', 
                  borderRadius: '12px', 
                  border: '2px solid #10b981',
                  width: '100%',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: '0 0 12px 0', color: '#10b981', fontWeight: 'bold' }}>
                    ✅ ¡Solicitud enviada correctamente!
                  </p>
                  <p className="meta" style={{ margin: '0 0 16px 0' }}>
                    Ahora puedes crear el parte de trabajo con todos los datos ya rellenados
                  </p>
                  
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button 
                      type="button" 
                      className="btn"
                      onClick={goToCreateWorkPart}
                      style={{ background: '#10b981' }}
                    >
                      📄 Crear Parte de Trabajo
                    </button>
                    
                    <button 
                      type="button" 
                      className="copy" 
                      onClick={startNewRequest}
                    >
                      ➕ Nueva Solicitud
                    </button>
                    
                    <button 
                      type="button" 
                      className="copy" 
                      onClick={() => navigate('/historial-solicitudes')}
                    >
                      📋 Ver Historial
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <p className="meta">
            * Campos obligatorios. La solicitud se enviará automáticamente por email al departamento administrativo.
          </p>
        </form>
      </div>
    </div>
  );
}